/**
 * `useRafLoop` — la boucle d'animation commune aux mini-jeux (SPEC-UI §5 : « boucle
 * `requestAnimationFrame` + `performance.now()` »).
 *
 * Elle donne le temps de jeu écoulé depuis le démarrage, en millisecondes. Aucun
 * `setInterval` : le timing d'un jeu de rythme ne se joue pas sur un timer qui dérive.
 *
 * **Le temps s'accumule frame par frame, il ne se lit pas sur l'horloge de départ.** La
 * différence est tout sauf cosmétique : quand l'onglet passe en arrière-plan, le navigateur
 * arrête `requestAnimationFrame` mais `performance.now()`, lui, continue. Avec un
 * `t - depart`, revenir sur l'onglet après vingt secondes ferait sauter le chrono à zéro et
 * **raterait tous les cuts d'un coup** — alt-tab = échec automatique, sur les cinq jeux. En
 * cumulant les deltas et en **ignorant** ceux qui dépassent `SEUIL_PAUSE`, le jeu se met en
 * pause tout seul et reprend là où il en était.
 */

import { useEffect, useRef } from "react";

/**
 * Au-delà de cet écart entre deux frames, on considère que le jeu n'était pas à l'écran
 * (onglet caché, machine en veille, gros hoquet) et on ne fait pas avancer le temps de jeu.
 * Une frame normale fait 16 ms ; même un à-coup sévère reste sous 250 ms.
 */
export const SEUIL_PAUSE_MS = 250;

export function useRafLoop(actif: boolean, aChaqueFrame: (ecouleMs: number) => void): void {
  const rappel = useRef(aChaqueFrame);
  rappel.current = aChaqueFrame;

  useEffect(() => {
    if (!actif) return;
    let brut = 0;
    let precedent = performance.now();
    let ecoule = 0;
    const frame = (t: number) => {
      const delta = t - precedent;
      precedent = t;
      if (delta < SEUIL_PAUSE_MS) ecoule += delta;
      rappel.current(ecoule);
      brut = requestAnimationFrame(frame);
    };
    brut = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(brut);
  }, [actif]);
}
